import type {
  AttributionResponse,
  ContactRequestPayload,
  ContactResponse,
  InteractionPage,
  SessionResponse,
  SubmissionResponse,
} from "./contract.js";

/** Narrow view of `fetch`, so tests can supply a stub without a global patch. */
export type FetchLike = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    credentials?: "same-origin" | "include" | "omit";
  },
) => Promise<{
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}>;

/** Failure the UI can render: always carries a human-readable message. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * Field-scoped messages from the server, e.g. `{ email: "must be a valid
     * email address" }`. Lets the form mark the offending input instead of
     * showing one generic message.
     */
    readonly details: Readonly<Record<string, string>> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type ApiClientOptions = {
  readonly baseUrl?: string;
  readonly fetch: FetchLike;
};

/**
 * The client's only knowledge of the server. Everything it can return is either
 * a `SubmissionResponse` or an {@link ApiError} — no raw responses escape.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchFn: FetchLike;
  private unauthenticatedHandler: (() => void) | undefined;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl ?? "";
    this.fetchFn = options.fetch;
  }

  /**
   * Register what to do when the server says the session is gone.
   *
   * Set after construction rather than passed in, because the thing that wants
   * to know is built from this client — and a mid-session expiry has to reach
   * the UI without every controller having to know about sessions.
   */
  onUnauthenticated(handler: () => void): void {
    this.unauthenticatedHandler = handler;
  }

  async submitPrompt(prompt: string): Promise<SubmissionResponse> {
    return this.request<SubmissionResponse>("POST", "/api/prompts", { prompt });
  }

  async submitContact(
    contact: ContactRequestPayload,
  ): Promise<ContactResponse> {
    return this.request<ContactResponse>("POST", "/api/contacts", contact);
  }

  /** Shapley attribution of output length to each section of the prompt. */
  async attributePrompt(prompt: string): Promise<AttributionResponse> {
    return this.request<AttributionResponse>("POST", "/api/attribution", {
      prompt,
    });
  }

  /** Who, if anyone, is signed in. A null `user` is a success, not a failure. */
  async getSession(): Promise<SessionResponse> {
    return this.request<SessionResponse>("GET", "/api/session");
  }

  async signIn(idToken: string): Promise<SessionResponse> {
    return this.request<SessionResponse>("POST", "/api/session", { idToken });
  }

  async signOut(): Promise<void> {
    await this.request<unknown>("DELETE", "/api/session");
  }

  /** A page of the activity log. Only administrators get past the server. */
  async fetchInteractions(
    options: { readonly limit?: number; readonly before?: string } = {},
  ): Promise<InteractionPage> {
    const query = new URLSearchParams();

    if (options.limit !== undefined) query.set("limit", String(options.limit));
    if (options.before !== undefined) query.set("before", options.before);

    const suffix = query.size === 0 ? "" : `?${query.toString()}`;

    return this.request<InteractionPage>("GET", `/api/admin/events${suffix}`);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const response = await this.fetchFn(`${this.baseUrl}${path}`, {
      method,
      headers: { "content-type": "application/json" },
      // Same-origin already sends cookies; stating it documents that the
      // session cookie is what authenticates every one of these calls.
      credentials: "same-origin",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    const payload = await readJson(response);

    if (!response.ok) {
      if (response.status === 401) this.unauthenticatedHandler?.();

      throw new ApiError(
        extractMessage(payload),
        response.status,
        extractDetails(payload),
      );
    }

    return payload as T;
  }
}

async function readJson(response: {
  json: () => Promise<unknown>;
}): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    // A non-JSON body (proxy error page, truncated response) must not surface
    // as an unhelpful parse error.
    return undefined;
  }
}

function errorObject(payload: unknown): Record<string, unknown> | undefined {
  if (typeof payload !== "object" || payload === null) return undefined;
  if (!("error" in payload)) return undefined;

  const error = (payload as { error: unknown }).error;
  if (typeof error !== "object" || error === null) return undefined;

  return error as Record<string, unknown>;
}

function extractDetails(payload: unknown): Record<string, string> {
  const details = errorObject(payload)?.details;

  if (typeof details !== "object" || details === null) return {};

  const result: Record<string, string> = {};
  for (const [field, message] of Object.entries(details)) {
    if (typeof message === "string") result[field] = message;
  }

  return result;
}

function extractMessage(payload: unknown): string {
  const message = errorObject(payload)?.message;

  return typeof message === "string"
    ? message
    : "The request failed. Please try again.";
}
